import { CheckIcon, Cross2Icon, PlusCircledIcon } from '@radix-ui/react-icons'
import type { Column } from '@tanstack/react-table'
import { cn } from 'lib/utils'

import { Badge } from 'primitives/badge'
import { Button } from 'primitives/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from 'primitives/command'
import { Popover, PopoverContent, PopoverTrigger } from 'primitives/popover'
import { Separator } from 'primitives/separator'
import { Tooltip } from 'primitives/tooltip'
import type React from 'react'

export interface Option {
  label: string
  value: string
  icon?: React.ComponentType<{ className?: string }>
  tooltip?: string
}

interface FacetedFilterProps<TData, TValue> {
  title?: string
  options: Option[]
  className?: string
  multiSelect?: boolean
  disabled?: boolean
  // For table column integration
  column?: Column<TData, TValue>
  // For manual state management
  value?: string | string[]
  onChange?: (value: string | string[] | undefined) => void
  // For external reset functionality
  onReset?: () => void
  // Whether to show external reset button when filters are active
  showResetButton?: boolean
}

export const FacetedFilter = <TData, TValue>({
  title,
  options,
  className,
  multiSelect = true,
  disabled = false,
  column,
  value: externalValue,
  onChange: externalOnChange,
  onReset,
  showResetButton = false,
}: FacetedFilterProps<TData, TValue>): React.ReactElement => {
  // Determine if we're using column-based or manual state management
  const isColumnMode = !!column

  // Get current filter value
  const filterValue = isColumnMode ? column.getFilterValue() : externalValue
  let selectedValuesArray: string[] = []

  if (multiSelect) {
    selectedValuesArray = Array.isArray(filterValue) ? filterValue : []
  } else if (filterValue && typeof filterValue === 'string') {
    selectedValuesArray = [filterValue]
  }
  const selectedValues = new Set(selectedValuesArray)

  // Handle value changes
  const handleValueChange = (newValue: string | string[] | undefined) => {
    if (isColumnMode) {
      column.setFilterValue(newValue)
    } else {
      externalOnChange?.(newValue)
    }
  }

  return (
    <div className="flex items-center space-x-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-[30px] font-inter text-s-400 normal-case tracking-normal"
            disabled={disabled}
          >
            <PlusCircledIcon className="mr-2 size-4" />
            {title}
            {selectedValues?.size > 0 && (
              <>
                <Separator orientation="vertical" className="mx-2 h-4" />
                <Badge
                  variant="secondary"
                  className="rounded-sm px-1 font-jetbrains text-xs-400 tabular-nums normal-case tracking-normal lg:hidden"
                >
                  {selectedValues.size}
                </Badge>
                <div className="hidden space-x-1 lg:flex">
                  {selectedValues.size > 2 ? (
                    <Badge
                      variant="secondary"
                      className="gap-1 rounded-sm px-1 font-inter text-s-400 normal-case tracking-normal"
                    >
                      <span className="font-jetbrains text-xs-400 tabular-nums">
                        {selectedValues.size}
                      </span>{' '}
                      selected
                    </Badge>
                  ) : (
                    options
                      .filter((option) => selectedValues.has(option.value))
                      .map((option) => (
                        <Badge
                          variant="secondary"
                          key={option.value}
                          className="rounded-sm px-1 font-inter text-s-400 normal-case tracking-normal"
                        >
                          {option.label}
                        </Badge>
                      ))
                  )}
                </div>
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className={cn('w-[12.5rem] p-0', className)} align="start">
          <Command>
            <CommandInput placeholder={title} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selectedValues.has(option.value)

                  const commandItem = (
                    <CommandItem
                      key={option.value}
                      onSelect={() => {
                        if (multiSelect) {
                          if (isSelected) {
                            selectedValues.delete(option.value)
                          } else {
                            selectedValues.add(option.value)
                          }
                          const filterValues = Array.from(selectedValues)
                          handleValueChange(filterValues.length ? filterValues : undefined)
                          return
                        }

                        // Single select mode
                        if (isSelected) {
                          handleValueChange(undefined)
                        } else {
                          handleValueChange(option.value)
                        }
                      }}
                    >
                      <div
                        className={cn(
                          'mr-2 flex size-4 items-center justify-center rounded-sm border border-primary',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'opacity-50 [&_svg]:invisible',
                        )}
                      >
                        <CheckIcon className="size-4" aria-hidden="true" />
                      </div>
                      {option.icon && (
                        <option.icon
                          className="mr-2 size-4 text-muted-foreground"
                          aria-hidden="true"
                        />
                      )}
                      <span>{option.label}</span>
                    </CommandItem>
                  )

                  return option.tooltip ? (
                    <Tooltip key={option.value} content={option.tooltip}>
                      <span className="w-full">{commandItem}</span>
                    </Tooltip>
                  ) : (
                    commandItem
                  )
                })}
              </CommandGroup>
              {selectedValues.size > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        handleValueChange(undefined)
                        onReset?.()
                      }}
                      className="justify-center text-center"
                    >
                      Clear filters
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {showResetButton && selectedValues.size > 0 && (
        <Button
          aria-label="Reset filters"
          variant="ghost"
          className="h-[30px] px-2 lg:px-3"
          onClick={() => {
            handleValueChange(undefined)
            onReset?.()
          }}
        >
          Reset
          <Cross2Icon className="ml-2 size-4" aria-hidden="true" />
        </Button>
      )}
    </div>
  )
}
